import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	Box,
	Button,
	FormControl,
	FormLabel,
	HStack,
	IconButton,
	Input,
	Select,
	Stack,
	Text,
	Textarea,
	useToast,
} from "@chakra-ui/react";
import { useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFieldArray, useForm } from "react-hook-form";
import { FiArrowRight, FiPlus, FiTrash2 } from "react-icons/fi";
import isEqual from "lodash/isEqual";
import { EducationLevel } from "src/__generated__/graphql";
import { logEvent } from "src/utlis/analytics";
import { PREFILL_PORTFOLIO_FROM_CV } from "./graphql";
import {
	getPortfolioInputIssueCount,
	getPortfolioInputSectionSummaries,
	portfolioInputReviewSchema,
	type PortfolioInputReviewFormValues,
} from "../portfolio-input-validation";
import { ResumeData } from "./types";

const QUALIFICATION_LABELS: Record<string, string> = {
	JSSCE: "Junior Secondary School Certificate (JSSCE)",
	SSCE: "Senior School Certificate (SSCE)",
	NABTEB: "NABTEB Certificate",
	KCSE: "KCSE",
	OND: "Ordinary National Diploma (OND)",
	HND: "Higher National Diploma (HND)",
	BACHELORS: "Bachelor's Degree",
	MASTERS: "Master's Degree",
	DOCTORATE: "Doctorate Degree",
};

function mapQualificationToEnum(q: string): EducationLevel {
	const mapping: Record<string, EducationLevel> = {
		JSSCE: EducationLevel.Jssce,
		SSCE: EducationLevel.Ssce,
		NABTEB: EducationLevel.Nabteb,
		KCSE: EducationLevel.Kcse,
		OND: EducationLevel.Ond,
		HND: EducationLevel.Hnd,
		BACHELORS: EducationLevel.Bachelors,
		MASTERS: EducationLevel.Masters,
		DOCTORATE: EducationLevel.Doctorate,
	};

	return mapping[q] || EducationLevel.Ssce;
}

interface PrefillReviewProps {
	resumeData: ResumeData;
	onComplete: () => void;
	onStartOver: () => void;
}

export const PrefillReview: React.FC<PrefillReviewProps> = ({
	resumeData,
	onComplete,
	onStartOver,
}) => {
	const toast = useToast();
	const [deletedCount, setDeletedCount] = useState(0);

	const defaultValues = useMemo<PortfolioInputReviewFormValues>(
		() => ({
			professionalSummary: resumeData.professionalSummary || "",
			educationItems: resumeData.educationItems.map((education) => ({
				schoolName: education.schoolName,
				fieldOfStudy: education.fieldOfStudy || undefined,
				qualification: mapQualificationToEnum(education.qualification),
			})),
			employmentItems: resumeData.employmentHistoryItems.map((employment) => ({
				companyName: employment.companyName,
				jobTitle: employment.jobTitle,
				description: employment.description,
				startedAt: employment.startedAt,
				endedAt: employment.endedAt || undefined,
			})),
			workHistoryItems: resumeData.projectHistoryItems.map((project) => ({
				name: project.name,
				description: project.description,
				startedAt: project.startedAt || undefined,
				endedAt: project.endedAt || "",
			})),
			certificationItems: resumeData.certificationItems.map((certification) => ({
				name: certification.name,
				issuedBy: certification.issuedBy,
				issuedAt: certification.issuedAt,
				issuerCertificationId:
					certification.issuerCertificationId || undefined,
				url: certification.url || undefined,
			})),
		}),
		[resumeData]
	);

	const originalData = useRef<PortfolioInputReviewFormValues>(
		structuredClone(defaultValues)
	);

	useEffect(() => {
		logEvent("cv_review_screen_viewed", { platform: "web" });
	}, []);

	const {
		register,
		control,
		handleSubmit,
		formState: { errors, submitCount },
	} = useForm<PortfolioInputReviewFormValues>({
		resolver: zodResolver(portfolioInputReviewSchema),
		defaultValues,
	});

	const {
		fields: employmentFields,
		append: appendEmployment,
		remove: removeEmployment,
	} = useFieldArray({
		control,
		name: "employmentItems",
	});

	const {
		fields: workHistoryFields,
		append: appendWorkHistory,
		remove: removeWorkHistory,
	} = useFieldArray({
		control,
		name: "workHistoryItems",
	});

	const {
		fields: educationFields,
		append: appendEducation,
		remove: removeEducation,
	} = useFieldArray({
		control,
		name: "educationItems",
	});

	const {
		fields: certificationFields,
		append: appendCertification,
		remove: removeCertification,
	} = useFieldArray({
		control,
		name: "certificationItems",
	});

	const [prefillPortfolio, { loading }] = useMutation(
		PREFILL_PORTFOLIO_FROM_CV,
		{
			onCompleted: (data) => {
				if (data.prefillPortfolioFromCV.laborpassCompleted) {
					logEvent("laborpass_completed", {
						completion_path: "cv",
						platform: "web",
					});
				}

				toast({
					title: "Portfolio updated",
					description:
						"Your CV details have been saved to your LaborPass.",
					status: "success",
					duration: 5000,
					isClosable: true,
				});
				onComplete();
			},
			onError: () => {
				toast({
					title: "Failed to save",
					description: "Something went wrong. Please try again.",
					status: "error",
					duration: 5000,
					isClosable: true,
				});
			},
			refetchQueries: ["MyPortfolioWithMedia"],
			awaitRefetchQueries: true,
		}
	);

	const hasAttemptedSave = submitCount > 0;
	const sectionSummaries = useMemo(
		() => getPortfolioInputSectionSummaries(errors),
		[errors]
	);
	const issueCount = useMemo(() => getPortfolioInputIssueCount(errors), [errors]);

	const handleRemoveItem = useCallback((remove: (index: number) => void, index: number) => {
		remove(index);
		setDeletedCount((count) => count + 1);
	}, []);

	const onSubmit = useCallback(
		(data: PortfolioInputReviewFormValues) => {
			logEvent("cv_data_confirmed", { platform: "web" });

			if (!isEqual(originalData.current, data)) {
				logEvent("cv_fields_edited_post_extraction", {
					platform: "web",
				});
			}

			if (deletedCount > 0) {
				logEvent("cv_entries_deleted_post_extraction", {
					entries_deleted_count: deletedCount,
					platform: "web",
				});
			}

			prefillPortfolio({
				variables: {
					professionalSummary: data.professionalSummary || undefined,
					educationItems: data.educationItems,
					employmentHistoryItems: data.employmentItems,
					workHistoryItems: data.workHistoryItems,
					certificationItems: data.certificationItems,
				},
			});
		},
		[deletedCount, prefillPortfolio]
	);

	return (
		<form onSubmit={handleSubmit(onSubmit)} className="h-full">
			<Stack
				spacing={0}
				className="flex flex-col lg:border-[1px] border-[#C9C9C9] bg-white h-auto lg:min-h-[70vh] justify-between"
			>
				<Stack
					spacing={6}
					px={{ base: 4, lg: 8 }}
					py={{ base: 6, lg: 12 }}
					overflowY="auto"
					flex={1}
				>
					<Box>
						<Text fontSize="2xl" fontWeight="bold" color="#121212">
							Review your CV details
						</Text>
						<Text color="gray.600" mt={1}>
							Review and edit the information extracted from your CV before saving
							it to your LaborPass.
						</Text>
					</Box>

					{hasAttemptedSave && issueCount > 0 && (
						<div
							role="alert"
							className="border border-[#F0C7C7] bg-[#FFF6F6] p-4"
						>
							<p className="text-sm font-semibold text-[#872B2B]">
								Please fix the highlighted fields before saving your LaborPass.
							</p>
							<div className="mt-3 space-y-1 text-sm text-[#A83A3A]">
								{sectionSummaries.map((section) => (
									<p key={section.section}>
										{section.section}: {section.count}{" "}
										{section.count === 1 ? "issue" : "issues"}
									</p>
								))}
							</div>
						</div>
					)}

					<SectionCard
						title="Professional Summary"
						extracted={!!resumeData.professionalSummary}
					>
						<FormControl>
							<Textarea
								{...register("professionalSummary")}
								placeholder="Share a short professional summary if you want to refine your profile."
								rounded={0}
								border="1px solid #D7D7D7"
								rows={5}
							/>
						</FormControl>
					</SectionCard>

					<SectionCard
						title="Employment History"
						extracted={resumeData.employmentHistoryItems.length > 0}
						onAdd={() =>
							appendEmployment({
								companyName: "",
								jobTitle: "",
								description: "",
								startedAt: "",
								endedAt: undefined,
							})
						}
					>
						{employmentFields.map((field, index) => (
							<Box key={field.id} p={4} border="1px solid #E7E7E7" mb={3}>
								<HStack justify="space-between" mb={3}>
									<Text fontWeight="medium" fontSize="sm">
										Employment {index + 1}
									</Text>
									<IconButton
										aria-label="Remove"
										icon={<FiTrash2 />}
										size="sm"
										variant="ghost"
										colorScheme="red"
										type="button"
										onClick={() => handleRemoveItem(removeEmployment, index)}
									/>
								</HStack>
								<Stack spacing={3}>
									<HStack spacing={3}>
										<FormControl
											isInvalid={!!errors.employmentItems?.[index]?.companyName}
										>
											<FormLabel fontSize="sm">Company</FormLabel>
											<Input
												{...register(
													`employmentItems.${index}.companyName` as const
												)}
												rounded={0}
												border="1px solid #D7D7D7"
												size="sm"
											/>
											<InlineFieldError
												message={
													errors.employmentItems?.[index]?.companyName?.message
												}
											/>
										</FormControl>
										<FormControl
											isInvalid={!!errors.employmentItems?.[index]?.jobTitle}
										>
											<FormLabel fontSize="sm">Job Title</FormLabel>
											<Input
												{...register(
													`employmentItems.${index}.jobTitle` as const
												)}
												rounded={0}
												border="1px solid #D7D7D7"
												size="sm"
											/>
											<InlineFieldError
												message={
													errors.employmentItems?.[index]?.jobTitle?.message
												}
											/>
										</FormControl>
									</HStack>
									<FormControl
										isInvalid={!!errors.employmentItems?.[index]?.description}
									>
										<FormLabel fontSize="sm">Description</FormLabel>
										<Textarea
											{...register(
												`employmentItems.${index}.description` as const
											)}
											rounded={0}
											border="1px solid #D7D7D7"
											size="sm"
											rows={4}
										/>
										<InlineFieldError
											message={
												errors.employmentItems?.[index]?.description?.message
											}
										/>
									</FormControl>
									<Stack spacing={3} direction={{ base: "column", md: "row" }}>
										<FormControl
											isInvalid={!!errors.employmentItems?.[index]?.startedAt}
										>
											<FormLabel fontSize="sm">Start Date</FormLabel>
											<Input
												type="date"
												{...register(
													`employmentItems.${index}.startedAt` as const
												)}
												rounded={0}
												border="1px solid #D7D7D7"
												size="sm"
											/>
											<InlineFieldError
												message={
													errors.employmentItems?.[index]?.startedAt?.message
												}
											/>
										</FormControl>
										<FormControl
											isInvalid={!!errors.employmentItems?.[index]?.endedAt}
										>
											<FormLabel fontSize="sm">End Date</FormLabel>
											<Input
												type="date"
												{...register(
													`employmentItems.${index}.endedAt` as const
												)}
												rounded={0}
												border="1px solid #D7D7D7"
												size="sm"
											/>
											<InlineFieldError
												message={
													errors.employmentItems?.[index]?.endedAt?.message
												}
											/>
										</FormControl>
									</Stack>
								</Stack>
							</Box>
						))}
						{employmentFields.length === 0 && (
							<Text fontSize="sm" color="gray.500">
								No employment history extracted. Click + to add.
							</Text>
						)}
					</SectionCard>

					<SectionCard
						title="Project History"
						extracted={resumeData.projectHistoryItems.length > 0}
						onAdd={() =>
							appendWorkHistory({
								name: "",
								description: "",
								startedAt: undefined,
								endedAt: "",
							})
						}
					>
						{workHistoryFields.map((field, index) => (
							<Box key={field.id} p={4} border="1px solid #E7E7E7" mb={3}>
								<HStack justify="space-between" mb={3}>
									<Text fontWeight="medium" fontSize="sm">
										Project {index + 1}
									</Text>
									<IconButton
										aria-label="Remove"
										icon={<FiTrash2 />}
										size="sm"
										variant="ghost"
										colorScheme="red"
										type="button"
										onClick={() => handleRemoveItem(removeWorkHistory, index)}
									/>
								</HStack>
								<Stack spacing={3}>
									<FormControl isInvalid={!!errors.workHistoryItems?.[index]?.name}>
										<FormLabel fontSize="sm">Project Name</FormLabel>
										<Input
											{...register(`workHistoryItems.${index}.name` as const)}
											rounded={0}
											border="1px solid #D7D7D7"
											size="sm"
										/>
										<InlineFieldError
											message={errors.workHistoryItems?.[index]?.name?.message}
										/>
									</FormControl>
									<FormControl
										isInvalid={!!errors.workHistoryItems?.[index]?.description}
									>
										<FormLabel fontSize="sm">Description</FormLabel>
										<Textarea
											{...register(
												`workHistoryItems.${index}.description` as const
											)}
											rounded={0}
											border="1px solid #D7D7D7"
											size="sm"
											rows={4}
										/>
										<InlineFieldError
											message={
												errors.workHistoryItems?.[index]?.description?.message
											}
										/>
									</FormControl>
									<Stack spacing={3} direction={{ base: "column", md: "row" }}>
										<FormControl>
											<FormLabel fontSize="sm">Start Date</FormLabel>
											<Input
												type="date"
												{...register(
													`workHistoryItems.${index}.startedAt` as const
												)}
												rounded={0}
												border="1px solid #D7D7D7"
												size="sm"
											/>
										</FormControl>
										<FormControl
											isInvalid={!!errors.workHistoryItems?.[index]?.endedAt}
										>
											<FormLabel fontSize="sm">End Date</FormLabel>
											<Input
												type="date"
												{...register(
													`workHistoryItems.${index}.endedAt` as const
												)}
												rounded={0}
												border="1px solid #D7D7D7"
												size="sm"
											/>
											<InlineFieldError
												message={
													errors.workHistoryItems?.[index]?.endedAt?.message
												}
											/>
										</FormControl>
									</Stack>
								</Stack>
							</Box>
						))}
						{workHistoryFields.length === 0 && (
							<Text fontSize="sm" color="gray.500">
								No projects extracted. Click + to add.
							</Text>
						)}
					</SectionCard>

					<SectionCard
						title="Education"
						extracted={resumeData.educationItems.length > 0}
						onAdd={() =>
							appendEducation({
								schoolName: "",
								qualification: EducationLevel.Ssce,
								fieldOfStudy: undefined,
							})
						}
					>
						{educationFields.map((field, index) => (
							<Box key={field.id} p={4} border="1px solid #E7E7E7" mb={3}>
								<HStack justify="space-between" mb={3}>
									<Text fontWeight="medium" fontSize="sm">
										Education {index + 1}
									</Text>
									<IconButton
										aria-label="Remove"
										icon={<FiTrash2 />}
										size="sm"
										variant="ghost"
										colorScheme="red"
										type="button"
										onClick={() => handleRemoveItem(removeEducation, index)}
									/>
								</HStack>
								<Stack spacing={3}>
									<FormControl
										isInvalid={!!errors.educationItems?.[index]?.schoolName}
									>
										<FormLabel fontSize="sm">School Name</FormLabel>
										<Input
											{...register(`educationItems.${index}.schoolName` as const)}
											rounded={0}
											border="1px solid #D7D7D7"
											size="sm"
										/>
										<InlineFieldError
											message={
												errors.educationItems?.[index]?.schoolName?.message
											}
										/>
									</FormControl>
									<FormControl
										isInvalid={!!errors.educationItems?.[index]?.qualification}
									>
										<FormLabel fontSize="sm">Qualification</FormLabel>
										<Select
											{...register(
												`educationItems.${index}.qualification` as const
											)}
											rounded={0}
											border="1px solid #D7D7D7"
											size="sm"
										>
											{Object.entries(QUALIFICATION_LABELS).map(
												([key, label]) => (
													<option key={key} value={key}>
														{label}
													</option>
												)
											)}
										</Select>
										<InlineFieldError
											message={
												errors.educationItems?.[index]?.qualification?.message
											}
										/>
									</FormControl>
									<FormControl
										isInvalid={!!errors.educationItems?.[index]?.fieldOfStudy}
									>
										<FormLabel fontSize="sm">Field of Study</FormLabel>
										<Input
											{...register(
												`educationItems.${index}.fieldOfStudy` as const
											)}
											placeholder="Optional"
											rounded={0}
											border="1px solid #D7D7D7"
											size="sm"
										/>
										<InlineFieldError
											message={
												errors.educationItems?.[index]?.fieldOfStudy?.message
											}
										/>
									</FormControl>
								</Stack>
							</Box>
						))}
						{educationFields.length === 0 && (
							<Text fontSize="sm" color="gray.500">
								No education extracted. Click + to add.
							</Text>
						)}
					</SectionCard>

					<SectionCard
						title="Certifications"
						extracted={resumeData.certificationItems.length > 0}
						onAdd={() =>
							appendCertification({
								name: "",
								issuedBy: "",
								issuedAt: "",
								issuerCertificationId: undefined,
								url: undefined,
							})
						}
					>
						{certificationFields.map((field, index) => (
							<Box key={field.id} p={4} border="1px solid #E7E7E7" mb={3}>
								<HStack justify="space-between" mb={3}>
									<Text fontWeight="medium" fontSize="sm">
										Certification {index + 1}
									</Text>
									<IconButton
										aria-label="Remove"
										icon={<FiTrash2 />}
										size="sm"
										variant="ghost"
										colorScheme="red"
										type="button"
										onClick={() =>
											handleRemoveItem(removeCertification, index)
										}
									/>
								</HStack>
								<Stack spacing={3}>
									<HStack spacing={3}>
										<FormControl
											isInvalid={!!errors.certificationItems?.[index]?.name}
										>
											<FormLabel fontSize="sm">Name</FormLabel>
											<Input
												{...register(
													`certificationItems.${index}.name` as const
												)}
												rounded={0}
												border="1px solid #D7D7D7"
												size="sm"
											/>
											<InlineFieldError
												message={
													errors.certificationItems?.[index]?.name?.message
												}
											/>
										</FormControl>
										<FormControl
											isInvalid={!!errors.certificationItems?.[index]?.issuedBy}
										>
											<FormLabel fontSize="sm">Issuer</FormLabel>
											<Input
												{...register(
													`certificationItems.${index}.issuedBy` as const
												)}
												rounded={0}
												border="1px solid #D7D7D7"
												size="sm"
											/>
											<InlineFieldError
												message={
													errors.certificationItems?.[index]?.issuedBy?.message
												}
											/>
										</FormControl>
									</HStack>
									<FormControl
										isInvalid={!!errors.certificationItems?.[index]?.issuedAt}
									>
										<FormLabel fontSize="sm">Issue Date</FormLabel>
										<Input
											type="date"
											{...register(
												`certificationItems.${index}.issuedAt` as const
											)}
											rounded={0}
											border="1px solid #D7D7D7"
											size="sm"
										/>
										<InlineFieldError
											message={
												errors.certificationItems?.[index]?.issuedAt?.message
											}
										/>
									</FormControl>
								</Stack>
							</Box>
						))}
						{certificationFields.length === 0 && (
							<Text fontSize="sm" color="gray.500">
								No certifications extracted. Click + to add.
							</Text>
						)}
					</SectionCard>
				</Stack>

				<Stack
					py={{ base: 4, lg: 8 }}
					px={{ base: 4, lg: 8 }}
					borderTop={{ base: "none", lg: "1px solid #E7E7E7" }}
					direction={{ base: "column", lg: "row" }}
					spacing={4}
					justifyContent="space-between"
					alignItems={{ base: "stretch", lg: "center" }}
				>
					<Button
						variant="ghost"
						type="button"
						onClick={() => {
							logEvent("cv_path_abandoned", {
								abandon_stage: "review",
								platform: "web",
							});
							onStartOver();
						}}
						isDisabled={loading}
						size="sm"
						w={{ base: "full", lg: "auto" }}
					>
						Start over with manual fill
					</Button>
					<Button
						type="submit"
						colorScheme="brand"
						rounded={0}
						px={8}
						py={6}
						rightIcon={<FiArrowRight size="18px" />}
						isLoading={loading}
						w={{ base: "full", lg: "auto" }}
					>
						Confirm & Save to LaborPass
					</Button>
				</Stack>
			</Stack>
		</form>
	);
};

interface SectionCardProps {
	title: string;
	extracted: boolean;
	onAdd?: () => void;
	children: React.ReactNode;
}

const SectionCard: React.FC<SectionCardProps> = ({
	title,
	extracted,
	onAdd,
	children,
}) => (
	<Box className="border border-[#E7E7E7] bg-white p-4 md:p-6">
		<div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
			<span className="w-fit self-start rounded-full bg-[#F7F2EE] px-3 py-1 text-xs font-semibold uppercase tracking-[0.08em] text-[#8C5A44] md:hidden">
				{extracted ? "Extracted" : "Add details"}
			</span>
			<HStack spacing={3}>
				<Text fontWeight="bold" fontSize="lg">
					{title}
				</Text>
				<span className="hidden w-fit rounded-full bg-[#F7F2EE] px-3 py-1 text-xs font-semibold uppercase tracking-[0.08em] text-[#8C5A44] md:inline-flex">
					{extracted ? "Extracted" : "Add details"}
				</span>
			</HStack>
			{onAdd && (
				<Button
					leftIcon={<FiPlus size="18px" />}
					colorScheme="earthRed"
					size="sm"
					variant="outline"
					type="button"
					onClick={onAdd}
					rounded={0}
				>
					Add
				</Button>
			)}
		</div>
		{children}
	</Box>
);

const InlineFieldError = ({ message }: { message?: string }) => {
	if (!message) {
		return null;
	}

	return <p className="mt-1 text-sm font-medium text-[#C93636]">{message}</p>;
};
